<template>
  <ExpansionPanels
    v-faq-view-tracker
    class="frequently-asked-questions"
    item-scope
    item-type="https://schema.org/FAQPage"
  >
    <template #title>
      <h2 class="frequently-asked-questions__title">{{ title }}</h2>
    </template>

    <template #default>
      <Item
        v-for="(item, index) in items"
        :key="item.question"
        v-faq-item-click-tracker="{ question: item.question }"
        :open="openSection[index]"
        @toggle="toggle(index, $event)"
      >
        <template #title>{{ item.question }}</template>
        <template #default>
          <div class="frequently-asked-questions__item-content" v-html="item.answerHtml" />
        </template>
      </Item>
    </template>
  </ExpansionPanels>
</template>

<script lang="ts" setup>
import { useExpansionPanelsController, ExpansionPanels, Item } from '@experience/core-expansion-panels-ui';

import { useAnalytics } from '@/shared/lib/analytics';

import type { FaqItem } from './models';

defineProps<{
  title: string;
  items: FaqItem[];
}>();

const { openSection, toggle } = useExpansionPanelsController();
const { useViewTracker, useClickTracker } = useAnalytics();
const vFaqViewTracker = useViewTracker('attraction-card:faq.view');
const vFaqItemClickTracker = useClickTracker('attraction-card:faq.click');
</script>

<style lang="scss" scoped>
.frequently-asked-questions__title {
  font-size: inherit;
  font-style: inherit;
  font-weight: inherit;
  line-height: inherit;

  @include base-800-750-extra;

  @include mobile {
    @include base-700-500-extra;
  }
}

.frequently-asked-questions__item-content {
  p,
  div {
    padding: 0;
    margin: 0;
  }

  ol,
  ul {
    display: flex;
    flex-direction: column;
    gap: var(--alias-space-16);
    padding-inline-start: var(--alias-space-24);
    padding-bottom: var(--alias-space-16);
    margin: var(--alias-space-16) 0 0;

    &:last-child {
      padding-bottom: 0;
    }

    li {
      &::marker {
        @include base-500-400-semi;

        @include mobile {
          @include base-400-400-semi;
        }
      }
    }
  }

  ol {
    list-style-type: decimal;
  }

  ul {
    list-style-type: disc;
  }

  strong {
    @include base-500-400-semi;

    @include mobile {
      @include base-400-400-semi;
    }
  }

  a,
  :deep(a) {
    color: var(--alias-color-text-default);
    text-decoration: none;
    cursor: pointer !important;
    border-bottom: 1px var(--alias-color-text-subtler) solid;
  }

  @include base-500-400-regular;

  @include mobile {
    gap: var(--alias-space-8);

    @include base-400-400-regular;
  }
}
</style>
